/**
 * Cart drawer.
 *
 * <cart-drawer>      — the drawer shell: open/close, focus handling, refreshing
 *                      its own markup through the Section Rendering API.
 * <cart-drawer-item> — a single line item: quantity changes and removal.
 *
 * Events listened for on `document`:
 *   cart:added   — something was added to the cart; refresh and open the drawer.
 *   cart:refresh — the cart changed elsewhere; refresh without opening.
 */

const SECTION_ID = 'cart-drawer';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const routes = (window.theme && window.theme.routes) || {};
const cartUrl = routes.cart_url || '/cart';
// The drawer section is not rendered on the cart page, so it is re-rendered
// from the root URL instead. Cart contents are the same on any page.
const sectionUrl = `${routes.root_url || '/'}?sections=${SECTION_ID}`;

// Passed to showError() when the request never reached Shopify, so that a
// network failure falls through to the same translated message as any other
// status the section has no specific string for.
const NO_RESPONSE = -1;

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.lastFocused = null;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('change', this.handleNoteChange);

    document.addEventListener('cart:added', this.handleCartAdded);
    document.addEventListener('cart:refresh', this.handleCartRefresh);
    document.addEventListener('click', this.handleToggleClick);

    if (!this.isOpen) this.setAttribute('inert', '');
  }

  disconnectedCallback() {
    document.removeEventListener('cart:added', this.handleCartAdded);
    document.removeEventListener('cart:refresh', this.handleCartRefresh);
    document.removeEventListener('click', this.handleToggleClick);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  get itemCount() {
    return parseInt(this.dataset.itemCount, 10) || 0;
  }

  handleCartAdded = () => this.refresh().then(() => this.open());

  handleCartRefresh = () => this.refresh();

  handleToggleClick = (event) => {
    const toggle = event.target.closest('[data-cart-drawer-toggle]');
    if (!toggle) return;

    event.preventDefault();
    this.open();
  };

  handleClick = (event) => {
    if (event.target.closest('[data-cart-drawer-close], [data-cart-drawer-overlay]')) {
      event.preventDefault();
      this.close();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.isOpen) return;

    // Keep focus inside the drawer while it is open.
    const focusable = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  handleNoteChange = (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;

    fetch(`${cartUrl}/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note.value })
    }).catch(() => {});
  };

  open() {
    if (this.isOpen) return;

    this.lastFocused = document.activeElement;
    this.removeAttribute('inert');
    this.classList.add('is-open');
    document.documentElement.classList.add('cart-drawer-open');

    const panel = this.querySelector('.cart-drawer__panel');
    if (panel) panel.focus();
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    document.documentElement.classList.remove('cart-drawer-open');
    this.setAttribute('inert', '');

    if (this.lastFocused && document.contains(this.lastFocused)) this.lastFocused.focus();
    this.lastFocused = null;
  }

  /** Re-renders the drawer contents from the server and syncs the header badge. */
  async refresh() {
    try {
      const response = await fetch(sectionUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.statusText);

      const sections = await response.json();
      const fresh = new DOMParser()
        .parseFromString(sections[SECTION_ID], 'text/html')
        .querySelector('cart-drawer');

      if (!fresh) return;

      const focus = this.describeFocus();

      this.innerHTML = fresh.innerHTML;
      this.dataset.itemCount = fresh.dataset.itemCount;
      this.updateBadge();

      // The element that had focus was just replaced.
      this.restoreFocus(focus);
    } catch {
      // Leave the current markup in place; the cart page remains the fallback.
    }
  }

  /**
   * Locates the focused control by line key rather than by node, so it can be
   * found again in the markup that replaces it — otherwise every quantity
   * change would drop a keyboard user back to the top of the drawer.
   */
  describeFocus() {
    const active = document.activeElement;
    if (!active || !this.contains(active)) return null;

    const item = active.closest('cart-drawer-item');
    if (!item) return null;

    const control = ['.plus-btn', '.minus-btn', '[data-cart-quantity-input]', '.cart-drawer__remove']
      .find((selector) => active.matches(selector));

    return control ? `cart-drawer-item[data-line-key="${item.dataset.lineKey}"] ${control}` : null;
  }

  restoreFocus(selector) {
    const target = selector && this.querySelector(selector);
    if (target) {
      target.focus();
      return;
    }

    if (this.isOpen && !this.contains(document.activeElement)) {
      this.querySelector('.cart-drawer__panel')?.focus();
    }
  }

  /** Shows a line-level message on the item that has just been re-rendered. */
  showItemError(lineKey, status) {
    this.querySelector(`cart-drawer-item[data-line-key="${lineKey}"]`)?.showError(status);
  }

  updateBadge() {
    document.querySelectorAll('.cart-count-badge').forEach((badge) => {
      badge.textContent = this.itemCount;
      badge.style.display = this.itemCount > 0 ? '' : 'none';
      badge.classList.remove('cart-count-badge--pop');
      void badge.offsetWidth;
      badge.classList.add('cart-count-badge--pop');
    });
  }
}

class CartDrawerItem extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
  }

  get drawer() {
    return this.closest('cart-drawer');
  }

  handleClick = (event) => {
    const button = event.target.closest('[data-cart-quantity]');
    if (!button) return;

    event.preventDefault();
    this.changeQuantity(parseInt(button.dataset.cartQuantity, 10));
  };

  handleChange = (event) => {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    this.changeQuantity(parseInt(input.value, 10));
  };

  async changeQuantity(quantity) {
    if (Number.isNaN(quantity) || quantity < 0) return;

    const drawer = this.drawer;
    const lineKey = this.dataset.lineKey;
    this.showError(0);
    if (drawer) drawer.classList.add('is-busy');

    try {
      const response = await fetch(`${cartUrl}/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });

      // A rejected request still changes the cart: Shopify answers 422 when it
      // cannot apply the quantity in full, having clamped the line to what is
      // in stock. Re-render either way, then report the failure.
      const status = response.ok ? 0 : response.status;

      if (drawer) {
        await drawer.refresh();
        if (status) drawer.showItemError(lineKey, status);
      } else {
        this.showError(status);
      }

      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch {
      // Network failure — nothing was re-rendered, so this row is still live.
      this.showError(NO_RESPONSE);
    } finally {
      if (drawer) drawer.classList.remove('is-busy');
    }
  }

  /**
   * Shows the translated message the section rendered for this HTTP status,
   * falling back to the generic one. A falsy status clears the message.
   */
  showError(status) {
    const target = this.querySelector('[data-cart-item-error]');
    if (!target) return;

    // getAttribute rather than dataset: `data-error-422` keeps its hyphen in
    // the dataset map, since only a hyphen before a letter is folded away.
    const message = status
      ? target.getAttribute(`data-error-${status}`) || target.getAttribute('data-error-default') || ''
      : '';

    target.textContent = message;
    target.hidden = !message;
  }
}

customElements.define('cart-drawer', CartDrawer);
customElements.define('cart-drawer-item', CartDrawerItem);
